import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { version as APP_VERSION } from '../../package.json';
import {
  Box, Typography, Avatar, Menu, MenuItem,
  ListItemIcon, ListItemText, useTheme, alpha, Tooltip,
  Divider, List, ListItem, ListItemButton,
  useMediaQuery, IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  VideoFile as LibraryIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  SettingsBrightness as SystemModeIcon,
  Videocam as VideocamIcon,
  Check as CheckIcon,
  Dns as ScreenerIcon,
  Shield as ShieldIcon,
  AdminPanelSettings as SiteAdminIcon,
  MonitorHeart as FleetHealthIcon,
  BugReport as BugReportIcon,
  MovieFilter as DCPIcon,
  Add as AddIcon,
  Folder as SourceIcon,
  CalendarMonth as CalendarIcon,
  Computer as NodesIcon,
  Theaters as VenueIcon,
  VideoSettings as ProfilesIcon,
  TextFields as TemplatesIcon,
  Business as CompaniesIcon,
  Storage as StorageIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  Send as DistributionIcon,
  History as AuditIcon,
  Groups as TeamIcon,
  Campaign as CampaignIcon,
  Image as KeyArtIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { logout } from '../store/slices/authSlice';
import { setActiveProduct } from '../store/slices/companySlice';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import UploadManagerOverlay from './UploadManagerOverlay';
import SitePresenceBeacon from './SitePresenceBeacon';
import BugReportFab from './BugReportFab';

const SIDEBAR_WIDTH = 220;
const SIDEBAR_COLLAPSED = 60;

// Map of pathname → human-readable browser-tab title. Falls back to
// "Screening Room" so the tab never reads as the generic "Watermarked
// Screeners" hard-coded in index.html when many tabs are open.
const PATH_TITLES = [
  [/^\/$/,                            'Dashboard'],
  [/^\/screeners\/create/,           'Create Watermark Encode'],
  [/^\/screeners/,                   'Watermarks Queue'],
  [/^\/dcp\/create/,                 'Create DCP'],
  [/^\/dcp\/[^/]+\/distribute/,      'KDM Distribution'],
  [/^\/dcp/,                         'DCP Queue'],
  [/^\/distribution/,                'Distribution'],
  // /library hosts both views — the title regex picks one statically
  // since we can't read Redux here. The PageHeader inside Library.jsx
  // is product-aware ("Distribution" for watermarks, "Library" for
  // DCP); this fallback only fires for the browser tab title.
  [/^\/library/,                     'Library'],
  [/^\/sources/,                     'Sources'],
  [/^\/calendar/,                    'Calendar'],
  [/^\/kdms/,                        'Distribution'],
  [/^\/venues/,                      'Venues'],
  [/^\/profile/,                     'Profile'],
  [/^\/settings\/workspace/,         'Workspace Settings'],
  [/^\/settings\/team/,              'Team'],
  [/^\/settings\/users/,             'Users'],
  [/^\/settings\/audit/,             'Audit Log'],
  [/^\/settings\/templates/,         'Watermark Templates'],
  [/^\/settings\/profiles/,          'Encoding Profiles'],
  [/^\/operator\/companies/,         'Operator · Companies'],
  [/^\/operator\/site-admins/,       'Operator · Site Admins'],
  [/^\/operator\/fleet-health/,      'Operator · Fleet Health'],
  [/^\/operator\/bug-reports/,       'Operator · Bug Reports'],
  [/^\/operator\/nodes/,             'Operator · Nodes'],
  [/^\/operator\/storage/,           'Operator · Storage'],
  [/^\/operator\/physical-drives/,   'Operator · Physical Drives'],
  [/^\/operator\/email/,             'Operator · Email'],
  [/^\/operator\/system/,            'Operator · System'],
  [/^\/operator\/audit/,             'Operator · Audit Log'],
  [/^\/pick-company/,                'Pick Workspace'],
];

function ScreeningRoomLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { themeMode, setTheme } = useCustomTheme();
  const { userInfo } = useSelector((state) => state.auth);
  const { activeCompanyId, activeProduct, list: myCompanies } = useSelector((state) => state.company);
  const [anchorEl, setAnchorEl] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  // v10.17.16: phone-width mode. On iPhone the fixed 220 px sidebar
  // eats more than half the viewport; collapsing-to-60 still leaves a
  // hostile narrow content column. Switch the sidebar to a drawer-style
  // overlay that defaults CLOSED and is opened by a hamburger button.
  // `md` breakpoint = 900 px so we capture phones AND tighter tablets.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close the drawer when the route changes — otherwise tapping a sidebar
  // link on mobile leaves the overlay sitting on top of the page they
  // navigated to.
  React.useEffect(() => {
    if (isMobile) setMobileNavOpen(false);
  }, [location.pathname, isMobile]);

  // Keep <title> in sync with the current route so multi-tab users can
  // identify each Screening Room tab at a glance. Title format:
  // "<Page> · Screening Room".
  React.useEffect(() => {
    const match = PATH_TITLES.find(([rx]) => rx.test(location.pathname));
    const page = match ? match[1] : 'Screening Room';
    document.title = match ? `${page} · Screening Room` : 'Screening Room';
  }, [location.pathname]);

  const isSuperadmin = !!userInfo?.is_superadmin;
  const activeCompany = myCompanies.find((c) => c.id === activeCompanyId);
  const memberRole = activeCompany?.role;
  const isCompanyAdmin = memberRole === 'admin' || isSuperadmin;
  const isVenueManager = memberRole === 'venue_manager';
  const hasPressReview = !!activeCompany?.has_press_review;
  const hasTheatricalDcp = !!activeCompany?.has_theatrical_dcp;
  // The active product's subgroup (Library/Create/Queue/...) stays visible
  // in the sidebar so the user can jump between sub-pages without losing
  // their place. Switching to the other product (or to operator mode)
  // collapses it.
  const showPressReview = activeProduct === 'press_review';
  const showTheatricalDcp = activeProduct === 'theatrical_dcp';
  const isDark = theme.palette.mode === 'dark';
  // Mobile: the sidebar is a drawer overlay; it doesn't take a column of
  // its own, so the layout's effective sidebar width is 0.
  const sidebarWidth = isMobile ? 0 : (collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH);
  // Drawer (mobile) renders full width when open. Desktop keeps the
  // existing collapsed-or-expanded inline column.
  const navColumnWidth = isMobile ? SIDEBAR_WIDTH : sidebarWidth;
  // The desktop "collapsed-to-60px" state is meaningless inside a mobile
  // drawer (the drawer is either full-width open or fully hidden). Force
  // it off so every `!collapsed && <Label/>` block renders its label.
  const navCollapsed = isMobile ? false : collapsed;

  const handleLogout = () => {
    setAnchorEl(null);
    dispatch(logout());
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    // Distribution sidebar highlights the new /distribution page AND the
    // legacy /dcp/N/distribute (per-DCP venue-batch KDM flow). The old
    // /kdms is gone — App.jsx redirects it to /distribution?tab=kdms.
    if (path === '/distribution') return location.pathname === '/distribution' || !!location.pathname.match(/^\/dcp\/\d+\/distribute/);
    // Exact match only — using a prefix match would highlight Queue when the
    // user is on Create (e.g., /screeners would match /screeners/create).
    return location.pathname === path;
  };

  // Operator mode = on /operator/* routes. Renders only the HHMG operator nav.
  const inOperatorMode = location.pathname.startsWith('/operator');

  // v10.19.27: a campaign must be picked before the rest of the
  // workspace surfaces unlock. Until then the sidebar shows only
  // Campaigns + (admin-only) Workspace settings, so the operator's
  // first action after picking a company is choosing the film.
  const hasCampaignPicked = (() => {
    try { return !!localStorage.getItem('activeCampaignId'); } catch (_e) { return false; }
  })();

  // Build sidebar based on the ACTIVE product. Only one product is visible at a time.
  const navGroups = inOperatorMode ? [
    {
      label: 'HHMG Operator',
      superadminOnly: true,
      items: [
        { text: 'Companies', icon: <CompaniesIcon />, path: '/operator/companies' },
        { text: 'Site Admins', icon: <SiteAdminIcon />, path: '/operator/site-admins' },
        { text: 'Fleet Health', icon: <FleetHealthIcon />, path: '/operator/fleet-health' },
        { text: 'Bug Reports', icon: <BugReportIcon />, path: '/operator/bug-reports' },
        { text: 'Encoders', icon: <NodesIcon />, path: '/operator/nodes' },
        { text: 'Storage', icon: <StorageIcon />, path: '/operator/storage' },
        { text: 'Physical Drives', icon: <StorageIcon />, path: '/operator/physical-drives' },
        { text: 'Email', icon: <EmailIcon />, path: '/operator/email' },
        { text: 'System', icon: <SettingsIcon />, path: '/operator/system' },
        { text: 'Global Audit', icon: <AuditIcon />, path: '/operator/audit' },
      ],
    },
  ] : isVenueManager ? [
    {
      items: [
        ...(hasTheatricalDcp ? [
          // Same workflow order as the operator sidebar: Library (browse
          // finished DCPs) → Distribution (send + KDMs) → Calendar +
          // Venues (planning / reference). Venue managers can't Create,
          // so the list starts at Library.
          { text: 'Library', icon: <LibraryIcon />, path: '/library' },
          { text: 'Distribution', icon: <DistributionIcon />, path: '/distribution' },
          { text: 'Calendar', icon: <CalendarIcon />, path: '/calendar' },
          { text: 'Venues', icon: <VenueIcon />, path: '/venues' },
        ] : []),
      ],
    },
  ] : [
    {
      items: [
        // v10.19.7: Campaigns sits at the top of the sidebar — it's the
        // first thing operators see post-login and the primary lens for
        // producer-facing reporting. Dashboard stays one slot down for
        // the system-health / queue view.
        { text: 'Campaigns', icon: <CampaignIcon />, path: '/campaigns' },
        // v10.19.30: Dashboard + Sources + product switchers all stay
        // hidden until a campaign is picked. Pre-campaign the sidebar
        // is just Campaigns — the operator's only meaningful action
        // is choosing the film. Picking a campaign sets
        // localStorage.activeCampaignId which unlocks the rest on the
        // next render.
        ...(hasCampaignPicked ? [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
          // v10.19.32: Key Art is per-campaign, so it appears in the
          // sidebar only after a campaign is picked. Page reads
          // activeCampaignId from localStorage.
          { text: 'Key Art', icon: <KeyArtIcon />, path: '/key-art' },
          { text: 'Sources', icon: <SourceIcon />, path: '/sources' },
        ] : []),
        // Permanent product switchers — visible whenever the company has the
        // product, regardless of which one is currently active.
        ...((hasCampaignPicked && hasPressReview) ? [{
          key: 'switch-press-review',
          text: 'Watermarks',
          icon: <ShieldIcon />,
          path: '/library',
          onClick: () => {
            dispatch(setActiveProduct('press_review'));
            navigate('/library');
          },
          // Stay highlighted whenever the user is browsing any Watermarks
          // sub-page (Library, Create, Queue, Templates, Profiles). Shared
          // pages (Dashboard, Sources, Workspace) clear the highlight.
          isActive: () => (
            activeProduct === 'press_review' &&
            location.pathname !== '/' &&
            location.pathname !== '/sources' &&
            !location.pathname.startsWith('/settings/workspace')
          ),
        }] : []),
        ...((hasCampaignPicked && hasTheatricalDcp) ? [{
          key: 'switch-theatrical-dcp',
          text: 'Theatrical DCP',
          icon: <DCPIcon />,
          path: '/library',
          onClick: () => {
            dispatch(setActiveProduct('theatrical_dcp'));
            navigate('/library');
          },
          isActive: () => (
            activeProduct === 'theatrical_dcp' &&
            location.pathname !== '/' &&
            location.pathname !== '/sources' &&
            !location.pathname.startsWith('/settings/workspace')
          ),
        }] : []),
      ],
    },
    // Both sidebars are workflow-ordered top-to-bottom: the operator
    // CREATES, watches the QUEUE, then sees finished work / manages
    // recipients. Reference data (templates, venues) sits at the bottom.
    // v10.19.27: Watermarks + Theatrical DCP product groups are hidden
    // until a campaign is picked — every job/DCP must land in one.
    ...((hasCampaignPicked && showPressReview) ? [{
      label: 'Watermarks',
      items: [
        { text: 'Create', icon: <AddIcon />, path: '/screeners/create' },
        { text: 'Queue', icon: <ScreenerIcon />, path: '/screeners' },
        // Watermarks has no separate library — every encode is per-
        // recipient, so this IS the distribution view. Path stays
        // /library so old bookmarks keep resolving.
        { text: 'Distribution', icon: <DistributionIcon />, path: '/library' },
        ...(isCompanyAdmin ? [
          { text: 'Templates', icon: <TemplatesIcon />, path: '/settings/templates' },
          { text: 'Profiles', icon: <ProfilesIcon />, path: '/settings/profiles' },
        ] : []),
      ],
    }] : []),
    ...((hasCampaignPicked && showTheatricalDcp) ? [{
      label: 'Theatrical DCP',
      items: [
        { text: 'Create', icon: <AddIcon />, path: '/dcp/create' },
        { text: 'Queue', icon: <DCPIcon />, path: '/dcp' },
        // Library is the catalog (every finished DCP); Distribution is
        // recipient + KDM management. Library before Distribution since
        // a DCP exists before it gets sent.
        { text: 'Library', icon: <LibraryIcon />, path: '/library' },
        { text: 'Distribution', icon: <DistributionIcon />, path: '/distribution' },
        // Calendar + Venues are reference / planning surfaces.
        { text: 'Calendar', icon: <CalendarIcon />, path: '/calendar' },
        { text: 'Venues', icon: <VenueIcon />, path: '/venues' },
      ],
    }] : []),
    // v10.19.28: workspace settings are also gated behind picking a
    // campaign — the operator should land on Campaigns first, every
    // time. Workspace surfaces (Settings / Users / Audit Log) remain
    // reachable by URL for admins so they can still manage from a
    // bookmark; the sidebar entries unlock alongside the rest.
    ...((hasCampaignPicked && isCompanyAdmin) ? [{
      label: 'Workspace',
      items: [
        { text: 'Settings', icon: <SettingsIcon />, path: '/settings/workspace' },
        { text: 'Team', icon: <TeamIcon />, path: '/settings/team' },
        { text: 'Users', icon: <AccountCircleIcon />, path: '/settings/users' },
        { text: 'Audit Log', icon: <AuditIcon />, path: '/settings/audit' },
      ],
    }] : []),
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile-only backdrop. Tapping it dismisses the nav drawer.
          Sits underneath the drawer (zIndex 1199 vs 1200) so it doesn't
          eat clicks on the nav itself. Only rendered when open AND on
          mobile — otherwise it'd intercept clicks on the desktop UI. */}
      {isMobile && mobileNavOpen && (
        <Box
          onClick={() => setMobileNavOpen(false)}
          sx={{
            position: 'fixed', inset: 0, zIndex: 1199,
            bgcolor: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          // On desktop the nav occupies a real column of the flex layout.
          // On mobile it's a drawer overlay, so the column width is 0.
          width: sidebarWidth, flexShrink: 0, transition: 'width 0.2s ease',
        }}
      >
        <Box
          sx={{
            width: navColumnWidth,
            height: '100vh',
            position: 'fixed',
            top: 0,
            // Mobile drawer: slide off-screen when closed.
            left: isMobile ? (mobileNavOpen ? 0 : -navColumnWidth - 8) : 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: isDark ? '#0d0f14' : '#fafafa',
            borderRight: `1px solid ${isDark ? 'rgba(212, 160, 23, 0.08)' : 'rgba(0,0,0,0.06)'}`,
            transition: 'left 0.22s ease, width 0.2s ease',
            overflow: 'hidden',
            zIndex: 1200,
            // Shadow only matters when overlaying content.
            boxShadow: (isMobile && mobileNavOpen)
              ? '4px 0 24px rgba(0,0,0,0.4)'
              : 'none',
          }}
        >
          {/* Logo */}
          <Box
            sx={{ p: navCollapsed ? 1.5 : 2, display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 52, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <VideocamIcon
              sx={{
                fontSize: 28,
                color: theme.palette.primary.main,
                filter: isDark ? 'drop-shadow(0 0 8px rgba(212, 160, 23, 0.3))' : 'none',
                flexShrink: 0,
              }}
            />
            {!navCollapsed && (
              <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: '-0.02em', fontSize: '0.9rem' }}>
                Screening Room
              </Typography>
            )}
          </Box>

          <Divider sx={{ borderColor: isDark ? 'rgba(212, 160, 23, 0.06)' : 'rgba(0,0,0,0.06)' }} />

          {/* Active workspace indicator (company or operator) */}
          {!navCollapsed && (activeCompany || inOperatorMode) && (
            <Box
              sx={{
                px: 2, py: 1.25,
                cursor: (myCompanies.length > 1 || isSuperadmin) ? 'pointer' : 'default',
                '&:hover': (myCompanies.length > 1 || isSuperadmin) ? { bgcolor: alpha(theme.palette.primary.main, 0.04) } : {},
                ...(inOperatorMode ? { borderLeft: `3px solid ${theme.palette.primary.main}` } : {}),
              }}
              onClick={() => {
                if (myCompanies.length > 1 || isSuperadmin) navigate('/pick-company');
              }}
            >
              <Typography variant="overline" sx={{ fontSize: '0.55rem', color: 'text.secondary', letterSpacing: '0.1em', display: 'block', lineHeight: 1 }}>
                {inOperatorMode ? 'Operator Mode' : 'Workspace'}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 600, mt: 0.25, color: inOperatorMode ? theme.palette.primary.main : 'inherit' }} noWrap>
                {inOperatorMode ? 'HHMG Operator' : activeCompany.name}
              </Typography>
              {!inOperatorMode && activeCompany.short_name && (
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                  {activeCompany.short_name}
                </Typography>
              )}
              {(myCompanies.length > 1 || isSuperadmin) && (
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: theme.palette.primary.main }}>
                  Switch workspace
                </Typography>
              )}
            </Box>
          )}
          {!navCollapsed && (activeCompany || inOperatorMode) && (
            <Divider sx={{ borderColor: isDark ? 'rgba(212, 160, 23, 0.06)' : 'rgba(0,0,0,0.06)' }} />
          )}

          {/* Navigation */}
          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 0.5 }}>
            {navGroups.map((group, gi) => (
              <Box key={gi} sx={{ mb: 0.5 }}>
                {!navCollapsed && group.label && (
                  <Tooltip
                    title={group.superadminOnly ? 'Visible to HHMG superadmins only — these settings affect the entire platform, not just the active company.' : ''}
                    placement="right"
                    arrow
                    disableHoverListener={!group.superadminOnly}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        px: 2, pt: 1.5, pb: 0.25,
                        display: 'block',
                        color: group.superadminOnly ? 'primary.main' : 'text.secondary',
                        fontSize: '0.65rem',
                        letterSpacing: '0.12em',
                        fontWeight: group.superadminOnly ? 700 : 400,
                        cursor: group.superadminOnly ? 'help' : 'default',
                      }}
                    >
                      {group.label}
                    </Typography>
                  </Tooltip>
                )}
                {navCollapsed && group.label && <Box sx={{ height: 6 }} />}
                <List disablePadding>
                  {group.items.map((item) => {
                    const active = item.isActive
                      ? item.isActive()
                      : isActive(item.path);
                    const handleClick = () => {
                      if (item.onClick) {
                        item.onClick();
                        return;
                      }
                      // Clicking "Dashboard" returns to the product chooser
                      if (item.path === '/') {
                        dispatch(setActiveProduct(null));
                      }
                      navigate(item.path);
                    };
                    return (
                      <ListItem key={item.key || item.path} disablePadding sx={{ px: 0.75 }}>
                        <Tooltip title={navCollapsed ? item.text : ''} placement="right" arrow>
                          <ListItemButton
                            onClick={handleClick}
                            sx={{
                              borderRadius: 1.5,
                              minHeight: 34,
                              px: navCollapsed ? 1.5 : 1.5,
                              justifyContent: navCollapsed ? 'center' : 'flex-start',
                              bgcolor: active ? alpha(theme.palette.primary.main, isDark ? 0.12 : 0.08) : 'transparent',
                              color: active ? theme.palette.primary.main : 'text.secondary',
                              '&:hover': {
                                bgcolor: active
                                  ? alpha(theme.palette.primary.main, isDark ? 0.16 : 0.12)
                                  : alpha(theme.palette.text.primary, 0.04),
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: navCollapsed ? 0 : 32, color: 'inherit', justifyContent: 'center' }}>
                              {React.cloneElement(item.icon, { sx: { fontSize: 18 } })}
                            </ListItemIcon>
                            {!navCollapsed && (
                              <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: active ? 600 : 400 }} />
                            )}
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            ))}
          </Box>

          {/* Bottom */}
          <Divider sx={{ borderColor: isDark ? 'rgba(212, 160, 23, 0.06)' : 'rgba(0,0,0,0.06)' }} />
          <Box sx={{ p: 0.75 }}>
            <SitePresenceBeacon collapsed={navCollapsed} />
            {isMobile ? (
              // Mobile drawer: "Close" dismisses the overlay. We never
              // collapse-to-60px in drawer mode — that's a desktop
              // affordance for a column-of-its-own sidebar.
              <ListItemButton
                onClick={() => setMobileNavOpen(false)}
                sx={{ borderRadius: 1.5, minHeight: 34, justifyContent: 'flex-start', px: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 32, justifyContent: 'center', color: 'text.secondary' }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </ListItemIcon>
                <ListItemText primary="Close" primaryTypographyProps={{ fontSize: '0.78rem', color: 'text.secondary' }} />
              </ListItemButton>
            ) : (
              <ListItemButton
                onClick={() => setCollapsed(!collapsed)}
                sx={{ borderRadius: 1.5, minHeight: 30, justifyContent: navCollapsed ? 'center' : 'flex-start', px: navCollapsed ? 1.5 : 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: navCollapsed ? 0 : 32, justifyContent: 'center', color: 'text.secondary' }}>
                  {navCollapsed ? <ExpandIcon sx={{ fontSize: 16 }} /> : <CollapseIcon sx={{ fontSize: 16 }} />}
                </ListItemIcon>
                {!navCollapsed && <ListItemText primary="Collapse" primaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary' }} />}
              </ListItemButton>
            )}

            <ListItemButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ borderRadius: 1.5, minHeight: 38, justifyContent: navCollapsed ? 'center' : 'flex-start', px: navCollapsed ? 1.5 : 1.5, mt: 0.25 }}
            >
              <Avatar
                sx={{
                  width: 24, height: 24,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '0.65rem', fontWeight: 600,
                  color: isDark ? '#0a0c10' : '#fff',
                }}
              >
                {userInfo?.username?.charAt(0).toUpperCase()}
              </Avatar>
              {!navCollapsed && (
                <Box sx={{ ml: 1.5, overflow: 'hidden' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem', lineHeight: 1.2 }} noWrap>
                    {userInfo?.full_name || userInfo?.username}
                  </Typography>
                </Box>
              )}
            </ListItemButton>
          </Box>
        </Box>
      </Box>

      {/* User menu */}
      <Menu
        anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        PaperProps={{ sx: { minWidth: 200, borderRadius: 2, ml: 1 } }}
      >
        <MenuItem onClick={() => { setAnchorEl(null); navigate('/profile'); }}>
          <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        {(myCompanies.length > 1 || isSuperadmin) && (
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/pick-company'); }}>
            <ListItemIcon><VideocamIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Switch workspace</ListItemText>
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="overline" sx={{ px: 2, display: 'block', color: 'text.secondary', fontSize: '0.55rem' }}>Theme</Typography>
        {[
          { mode: 'light', icon: <LightModeIcon fontSize="small" />, label: 'Light' },
          { mode: 'dark', icon: <DarkModeIcon fontSize="small" />, label: 'Dark' },
          { mode: 'system', icon: <SystemModeIcon fontSize="small" />, label: 'System' },
        ].map(({ mode: m, icon, label }) => (
          <MenuItem key={m} onClick={() => setTheme(m)}>
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText>{label}</ListItemText>
            {themeMode === m && <CheckIcon fontSize="small" sx={{ ml: 'auto', color: 'primary.main' }} />}
          </MenuItem>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1, minHeight: '100vh',
          // On mobile, the main column owns the full viewport because
          // the nav is a drawer overlay. Without `minWidth: 0` the flex
          // layout would let wide children (tables, code blocks) push
          // the column past the viewport, which is exactly the
          // "right side doesn't fit" symptom on iPhone.
          minWidth: 0,
          display: 'flex', flexDirection: 'column',
          bgcolor: isDark ? theme.palette.background.default : alpha(theme.palette.background.default, 0.5),
        }}
      >
        {/* Mobile-only header row holds the hamburger to open the nav
            drawer. Desktop's nav is always visible as an inline column,
            so this header doesn't render there. Sticky so the
            hamburger stays reachable while scrolling long pages. */}
        {isMobile && (
          <Box
            sx={{
              position: 'sticky', top: 0, zIndex: 1100,
              display: 'flex', alignItems: 'center',
              px: 1, py: 0.5,
              bgcolor: isDark ? alpha('#0d0f14', 0.92) : alpha('#ffffff', 0.94),
              backdropFilter: 'blur(8px)',
              borderBottom: `1px solid ${isDark ? 'rgba(212, 160, 23, 0.06)' : 'rgba(0,0,0,0.05)'}`,
            }}
          >
            <IconButton
              aria-label="Open navigation"
              onClick={() => setMobileNavOpen(true)}
              size="medium"
              sx={{ color: theme.palette.primary.main }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 700, fontSize: '0.85rem' }}>
              Screening Room
            </Typography>
          </Box>
        )}
        {/* Bottom padding equal to the fixed-footer height so the last
            row of any page isn't hidden underneath the footer. Bumped
            slightly to clear the BugReportFab's hover state too. On
            mobile the page padding shrinks so narrow viewports don't
            give 24 px back to the gutter on every side. */}
        <Box sx={{ flexGrow: 1, p: isMobile ? 1.5 : 3, pb: 7, maxWidth: 1400, minWidth: 0 }}>
          <Outlet />
        </Box>
        {/* Fixed to the viewport, not the document. Operator wants the
            footer always visible regardless of scroll position. Offset
            by sidebarWidth so it lines up with the content column and
            doesn't sit under the nav. */}
        <Box
          component="footer"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: sidebarWidth,
            right: 0,
            zIndex: 1100,
            py: 1, px: 3,
            bgcolor: isDark ? alpha('#0d0f14', 0.92) : alpha('#ffffff', 0.94),
            backdropFilter: 'blur(8px)',
            borderTop: `1px solid ${isDark ? 'rgba(212, 160, 23, 0.06)' : 'rgba(0,0,0,0.04)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            transition: 'left 0.2s ease',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.03em', fontSize: '0.6rem' }}>
              Screening Room v{APP_VERSION} — HHMG
            </Typography>
            {/* Operator-presence chip used to live here; merged into the
                sidebar's SitePresenceBeacon as of v10.17.12 so there's a
                single answer to "who's on the site" instead of three. */}
          </Box>
          <BugReportFab />
        </Box>
      </Box>
      <UploadManagerOverlay />
    </Box>
  );
}

export default ScreeningRoomLayout;
